import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Button, Divider, Grid, Header, Segment } from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { functionLinkTransactionWithAgent } from "../../../app/firestore/functionsService";
import MyTextInput from "../../../app/common/form/MyTextInput";
import MyTextArea from "../../../app/common/form/MyTextArea";
import { updatePartyInDb } from "../../../app/firestore/firestoreService";

export default function TransactionLinkingModal({ party, transaction }) {
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const initialValues = {
    ...transaction,
    mlsNumbers: [
      transaction.mlsNumbers?.[0] || "",
      transaction.mlsNumbers?.[1] || "",
    ],
    propertyDetails: {
      ...transaction.propertyDetails,
      legalDescription: transaction.propertyDetails?.legalDescription || "",
    },
  };

  return (
    <>
      <ModalWrapper size="small">
        <Segment>
          <Grid>
            <Grid.Column>
              <Header size="large" color="blue">
                Link Transaction
              </Header>
              <Divider />
              <p className="text-large text blue bold ">
                Good news! {party.firstName} {party.lastName} is also using
                TransActioner.
              </p>
              <p className="text-medium small bottom margin">
                We can link the transactions so documents show up automatically
                in their transaction. Please make sure the MLS number(s) and
                legal description below are correct and then hit the "Link
                Transaction" button.
              </p>
              <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    if (
                      !values.mlsNumbers?.[0] &&
                      !values.mlsNumbers?.[1] &&
                      !values.propertyDetails?.legalDescription
                    ) {
                      toast.error(
                        "Please enter MLS numbers or legal description"
                      );
                    } else {
                      const result = await functionLinkTransactionWithAgent({
                        party: party,
                        values: values,
                      });
                      if (result?.error) {
                        toast.error(result.error);
                      } else if (result?.transactionId) {
                        await updatePartyInDb(party, {
                          isLinked: true,
                          linkedTransactionId: result.transactionId,
                        });
                        toast.success("Transaction linked successfully!");
                        dispatch(
                          closeModal({
                            modalType: "TransactionLinkingModal",
                          })
                        );
                      } else {
                        toast.error(
                          "Unable to find a matching transaction. Make sure the other agent has the same MLS number or legal description set up."
                        );
                      }
                    }
                  } catch (error) {
                    toast.error(error.message);
                  } finally {
                    if (isMountedRef.current) {
                      setSubmitting(false);
                    }
                  }
                }}
              >
                {({ isSubmitting, isValid }) => (
                  <Form className="ui form" autoComplete="off">
                    <Grid stackable>
                      <Grid.Column mobile={16} computer={16}>
                        <Grid className="zero vertical padding zero top margin">
                          <Grid.Row className="zero bottom padding">
                            <Grid.Column mobile={16} computer={8}>
                              <MyTextInput name="mlsNumbers[0]" label="MLS #" />
                            </Grid.Column>
                            <Grid.Column mobile={16} computer={8}>
                              <MyTextInput
                                name="mlsNumbers[1]"
                                label="MLS # (secondary)"
                              />
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                        <Grid>
                          <Grid.Row>
                            <Grid.Column width={16}>
                              <MyTextArea
                                rows="3"
                                name="propertyDetails.legalDescription"
                                label="Legal description"
                              />
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      </Grid.Column>
                    </Grid>
                    <Divider className="medium top margin" />
                    <Button
                      loading={isSubmitting}
                      disabled={!isValid || isSubmitting}
                      type="submit"
                      floated={isMobile ? null : "right"}
                      primary
                      content="Link Transaction"
                      className={isMobile ? "fluid medium bottom margin" : null}
                    />
                    <Button
                      disabled={isSubmitting}
                      onClick={() =>
                        dispatch(
                          closeModal({
                            modalType: "TransactionLinkingModal",
                          })
                        )
                      }
                      to="#"
                      type="button"
                      floated={isMobile ? null : "right"}
                      content="Cancel"
                      className={isMobile ? "fluid medium" : null}
                    />
                  </Form>
                )}
              </Formik>
            </Grid.Column>
          </Grid>
        </Segment>
      </ModalWrapper>
    </>
  );
}

